import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { Avatar, Box, Chip, Dialog, Divider, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DrawerCustom from "../../../shared/general/Drawer";
import StatusRequest from "../../../shared/general/stausRequest";

dayjs.extend(customParseFormat);

const StyledRoot = styled("div")({
  width: 480,
  maxWidth: "100%",
  padding: 24,
  "& .header": {
    display: "flex",
    alignItems: "center",
    marginBottom: 16,
    "& .MuiAvatar-root": { width: 56, height: 56, marginRight: 12 },
    "& .name": { fontSize: 18, fontWeight: 600, lineHeight: 1.2 },
    "& .position": { color: "#9e9e9e", fontSize: 14 },
  },
  "& .section": { marginBottom: 18 },
  "& .topic": { color: "#9e9e9e", fontSize: 13, marginBottom: 2 },
  "& .value": { fontWeight: 500 },
  "& .approval-title": { fontSize: 15, fontWeight: 600, marginBottom: 12 },
  "& .step": {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: 14,
    "& .step-body": { marginLeft: 12, flexGrow: 1 },
    "& .step-label": { color: "#9e9e9e", fontSize: 12 },
    "& .step-name": { fontWeight: 500 },
    "& .step-email": { color: "#9e9e9e", fontSize: 13 },
    "& .step-date": { color: "#9e9e9e", fontSize: 13 },
    "& .step-comment": {
      marginTop: 4,
      padding: "6px 10px",
      background: "#fdf3f5",
      borderRadius: 8,
      fontSize: 13,
    },
  },
  "& .attachment": {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    "& .thumb": {
      width: 120,
      height: 120,
      borderRadius: 8,
      border: "1px solid #e0e0e0",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#f4f6f8",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "& img": { width: "100%", height: "100%", objectFit: "cover" },
      "& svg": { fontSize: 48, color: "#8a94a6" },
    },
    "&:hover .thumb": {
      borderColor: "#007afe",
      boxShadow: "0 2px 8px rgba(0,122,254,0.2)",
    },
    "& .filename": {
      marginTop: 6,
      fontSize: 12,
      color: "#637381",
      maxWidth: 140,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
});

const isImageFile = (name) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || "");
const isPdfFile = (name) => /\.pdf$/i.test(name || "");

const fullName = (r) => `${r.firstname || ""} ${r.lastname || ""}`.trim() || "-";

const toDateText = (value) => {
  if (!value) return null;
  if (dayjs(value, "DD/MM/YYYY HH:mm", true).isValid()) return value;
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : null;
};

const renderOT = (request) => {
  const rows = [];
  const push = (label, minutes) => {
    if (minutes > 0) {
      rows.push(
        <Typography key={label} className="value">
          {label} = {parseFloat(minutes / 60).toFixed(2)} ชม.
        </Typography>
      );
    }
  };
  if (request.xOTSetting) push(`OT x ${request.xOTSetting}`, request.xOT);
  if (request.xWorkingDailyHolidaySetting)
    push(`OT x ${request.xWorkingDailyHolidaySetting}`, request.xWorkingDailyHoliday);
  if (request.xWorkingMonthlyHolidaySetting)
    push(`OT x ${request.xWorkingMonthlyHolidaySetting}`, request.xWorkingMonthlyHoliday);
  if (request.xOTHolidaySetting)
    push(`OT x ${request.xOTHolidaySetting}`, request.xOTHoliday);
  if (request.xWorkingDaily > 0) push("OT x 1", request.xWorkingDaily);
  return rows.length > 0 ? rows : null;
};

const buildSteps = (request) => {
  const isLeave = Boolean(request.idLeave);
  const steps = [];
  const add = (level, name, email, isApprove, date, comment) => {
    if (!name && isApprove === undefined) return;
    steps.push({ level, name: name || "-", email, isApprove, date, comment });
  };

  if (isLeave) {
    const decide = request.approvalLevel;
    if (request.managerLV1ApproveBy || request.managerLV1Name) {
      add(
        1,
        request.managerLV1Name,
        request.emailManagerLV1,
        decide === 1 ? request.isApprove : request.isManagerLV1Approve,
        decide === 1 ? request.approveDateText : request.managerLV1ApproveDate,
        decide === 1 ? request.commentManager : request.commentManagerLV1
      );
    }
    if (request.managerLV2ApproveBy || request.managerLV2Name) {
      add(
        2,
        request.managerLV2Name,
        request.emailManagerLV2,
        decide === 2 ? request.isApprove : request.isManagerLV2Approve,
        decide === 2 ? request.approveDateText : request.managerLV2ApproveDate,
        decide === 2 ? request.commentManager : request.commentManagerLV2
      );
    }
    if (steps.length === 0) {
      add(
        null,
        request.managerLV1Name || request.managerName,
        request.emailManagerLV1,
        request.isApprove,
        request.approveDateText,
        request.commentManager
      );
    }
  } else if (request.isDoubleApproval) {
    add(1, request.managerLV1Name, request.emailManagerLV1, request.isManagerLV1Approve, request.managerLV1ApproveDate, request.commentManagerLV1);
    add(2, request.managerLV2Name, request.emailManagerLV2, request.isManagerLV2Approve, request.managerLV2ApproveDate, request.commentManagerLV2);
  } else if (request.approvalLevel === 2) {
    add(2, request.managerLV2Name, request.emailManagerLV2, request.isManagerLV2Approve, request.managerLV2ApproveDate, request.commentManagerLV2);
  } else {
    add(1, request.managerLV1Name, request.emailManagerLV1, request.isManagerLV1Approve, request.managerLV1ApproveDate, request.commentManagerLV1);
  }
  return steps;
};

const getOverallStatus = (request) => {
  if (request.idLeaveEmployeesWithdraw || request.idRequestTimeWithdraw)
    return "withdraw";
  if (request.isActive === 0) return "withdraw";
  if (request.idLeave) {
    if (request.isApprove === 1) return "approved";
    if (request.isApprove === 0) return "notApproved";
    return "pending";
  }
  const decided =
    request.approvalLevel === 2
      ? request.isManagerLV2Approve
      : request.isManagerLV1Approve;
  if (decided === 1) return "approved";
  if (decided === 0) return "notApproved";
  return "pending";
};

const STATUS_CHIP = {
  approved: { label: "อนุมัติ", color: "#00c292", bg: "#e6f8f3" },
  notApproved: { label: "ไม่อนุมัติ", color: "#e46a76", bg: "#fdf3f5" },
  withdraw: { label: "ถอน", color: "#8a94a6", bg: "#f3f6f8" },
  pending: { label: "รออนุมัติ", color: "#f0a500", bg: "#fff8e6" },
};

function Section({ label, children }) {
  if (!children) return null;
  return (
    <div className="section">
      <Typography className="topic">{label}</Typography>
      {typeof children === "string" ? (
        <Typography className="value">{children}</Typography>
      ) : (
        children
      )}
    </div>
  );
}

function RequestDialog(props) {
  const { openDialog, setOpenDialog } = props;
  const request = openDialog.request || {};
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleCloseDialog = () => {
    setOpenDialog({ ...openDialog, isOpen: false });
  };

  const isLeave = Boolean(request.idLeave);
  const isOT = request.idRequestType === 2 || request.idRequestType === 3;
  const steps = buildSteps(request);
  const chip = STATUS_CHIP[getOverallStatus(request)];

  const hasFile = Boolean(request.filename && request.fileURL);
  const fileIsImage = isImageFile(request.filename);
  const fileIsPdf = isPdfFile(request.filename);

  return (
    <DrawerCustom
      title={"รายละเอียด"}
      anchor={"right"}
      open={openDialog.isOpen}
      onClose={handleCloseDialog}
    >
      <StyledRoot>
        <div className="header">
          <Avatar>{(request.firstname || "?").charAt(0)}</Avatar>
          <Box flexGrow={1}>
            <Typography className="name">{fullName(request)}</Typography>
            {request.positionsName && (
              <Typography className="position">{request.positionsName}</Typography>
            )}
          </Box>
          {chip && (
            <Chip
              label={chip.label}
              size="small"
              style={{ color: chip.color, backgroundColor: chip.bg, fontWeight: 600 }}
            />
          )}
        </div>
        <Divider style={{ marginBottom: 18 }} />

        <Section label="ประเภท">
          {(request.idLeaveEmployeesWithdraw || request.idRequestTimeWithdraw) && (
            <Chip
              label="ถอนรายการ"
              size="small"
              style={{ marginRight: 8, backgroundColor: "#ff3034", color: "#fff", height: 20 }}
            />
          )}
          {request.name || "-"}
        </Section>

        <Section label="ช่วงเวลา">
          {request.startText
            ? !request.endText || request.startText === request.endText
              ? request.startText
              : `${request.startText} - ${request.endText}`
            : "-"}
        </Section>

        {isLeave && (request.used || request.used === 0) && (
          <Section label="จำนวนที่ใช้ไป (วัน)">{`${request.used}`}</Section>
        )}

        {isOT && <Section label="ชั่วโมงทำงานล่วงเวลา">{renderOT(request)}</Section>}

        <Section label="เหตุผล">
          {isLeave ? (
            request.description || "-"
          ) : request.requestReasonName || request.otherReason ? (
            <>
              {request.requestReasonName && (
                <Typography className="value">{request.requestReasonName}</Typography>
              )}
              {request.otherReason && (
                <Typography color="text.secondary">{request.otherReason}</Typography>
              )}
            </>
          ) : (
            request.description || "-"
          )}
        </Section>

        {(request.createDateText || request.createLeaveText) && (
          <Section label="วันเวลาที่ทำรายการ">
            {request.createDateText || request.createLeaveText}
          </Section>
        )}

        <Section label="เอกสารแนบ">
          {hasFile ? (
            <Box className="attachment" onClick={() => setPreviewOpen(true)}>
              <Box className="thumb">
                {fileIsImage ? (
                  <img src={request.fileURL} alt={request.filename} />
                ) : fileIsPdf ? (
                  <PictureAsPdfIcon />
                ) : (
                  <InsertDriveFileIcon />
                )}
              </Box>
              <Typography className="filename">{request.filename}</Typography>
            </Box>
          ) : (
            "-"
          )}
        </Section>

        {steps.length > 0 && (
          <>
            <Divider style={{ margin: "8px 0 18px" }} />
            <Typography className="approval-title">การอนุมัติ</Typography>
            {steps.map((step, index) => (
              <div className="step" key={index}>
                <StatusRequest
                  boxSize={36}
                  fontSize={20}
                  borderRadius={8}
                  status={
                    step.isApprove === 1 ? 1 : step.isApprove === 0 ? 0 : null
                  }
                  active={1}
                />
                <div className="step-body">
                  {step.level && (
                    <Typography className="step-label">
                      ผู้อนุมัติลำดับที่ {step.level}
                    </Typography>
                  )}
                  <Typography className="step-name">{step.name}</Typography>
                  {step.email && (
                    <Typography className="step-email">{step.email}</Typography>
                  )}
                  {toDateText(step.date) && (
                    <Typography className="step-date">
                      {toDateText(step.date)}
                    </Typography>
                  )}
                  {step.isApprove === 0 && step.comment && (
                    <Typography className="step-comment">{step.comment}</Typography>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {hasFile && (
          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <Box style={{ position: "relative" }}>
              <IconButton
                aria-label="close"
                onClick={() => setPreviewOpen(false)}
                style={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  zIndex: 1,
                  backgroundColor: "rgba(255,255,255,0.85)",
                }}
              >
                <CloseIcon />
              </IconButton>
              {fileIsImage ? (
                <img
                  src={request.fileURL}
                  alt={request.filename}
                  style={{ width: "100%", display: "block" }}
                />
              ) : fileIsPdf ? (
                <iframe
                  title="preview"
                  src={request.fileURL}
                  style={{ width: "100%", height: "80vh", border: 0 }}
                />
              ) : (
                <Box style={{ padding: 40, textAlign: "center" }}>
                  <InsertDriveFileIcon
                    style={{ fontSize: 64, color: "#8a94a6" }}
                  />
                  <Typography style={{ marginTop: 8 }}>
                    ไม่สามารถแสดงตัวอย่างไฟล์นี้ได้
                  </Typography>
                </Box>
              )}
              <Box style={{ padding: 12, textAlign: "right" }}>
                <a
                  href={request.fileURL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  เปิดในแท็บใหม่
                </a>
              </Box>
            </Box>
          </Dialog>
        )}
      </StyledRoot>
    </DrawerCustom>
  );
}

export default RequestDialog;
